import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { saveIotSchedule } from '../../../../api/iot';
import { errorMessages } from '../../../../utils/methods';
import { sendWithOfflineConfirm, OFFLINE_SAVE_MESSAGE } from '../iotOffline';
import { StyledSwitch } from '../FacilityControlStyled';
import WeeklyScheduleCard from './WeeklyScheduleCard';
import {
  Wrapper,
  TopBar,
  TopBarTitle,
  CardGrid,
  EmptyMessage,
  Footer,
  AddScheduleButton,
  SaveButton,
} from './WeeklyScheduleStyled';

// 요일 정렬 순서 (월~일)
const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABEL = {
  monday: '월',
  tuesday: '화',
  wednesday: '수',
  thursday: '목',
  friday: '금',
  saturday: '토',
  sunday: '일',
};

// 시:분 → 분
const toMinutes = ({ hour, minute }) => parseInt(hour, 10) * 60 + parseInt(minute, 10);
// "HH:MM" → { hour, minute }
const parseTime = time => ({ hour: time.split(':')[0] || '00', minute: time.split(':')[1] || '00' });
// 다음 요일 키 (일요일 → 월요일 순환)
const nextDayKey = dayKey => DAYS_ORDER[(DAYS_ORDER.indexOf(dayKey) + 1) % DAYS_ORDER.length];
// OFF 가 ON 보다 같거나 빠르면 자정을 넘기는 "익일 OFF"
const isOvernight = (onTime, offTime) => toMinutes(offTime) <= toMinutes(onTime);

// (lights, timeSlot 시간들) 동등성 해시 — 요일 간 병합 비교용
const hashCardContent = (lights, timeSlots) =>
  JSON.stringify({
    lights: [...lights].sort((a, b) => a - b),
    slots: timeSlots.map(s => `${s.onTime.hour}:${s.onTime.minute}-${s.offTime.hour}:${s.offTime.minute}`).sort(),
  });

// scheduleData (서버 응답)을 카드 배열로 변환
// 서버는 요일별 on/off 이벤트 나열이고 OFF 는 다음 날에 있을 수 있다(익일 OFF).
// 그래서 요일 안에서 페어링하지 않고, 릴레이별로 "주(week) 타임라인"을 만들어
// 시각순으로 on→off 를 묶는다(주 경계 순환). 슬롯의 요일 = ON 이벤트가 속한 요일.
const buildSchedulesFromServer = scheduleData => {
  if (!scheduleData?.rlyTime) return [];

  // 1) 릴레이(rly 문자열)별로 한 주의 모든 이벤트 수집 (요일 인덱스 포함 절대 분)
  const eventsByRly = {}; // rlyKey -> [{ dayIndex, absMinute, time, onOff }]
  DAYS_ORDER.forEach((dayKey, dayIndex) => {
    (scheduleData.rlyTime[dayKey] || []).forEach(entry => {
      if (!eventsByRly[entry.rly]) eventsByRly[entry.rly] = [];
      eventsByRly[entry.rly].push({
        dayIndex,
        absMinute: dayIndex * 1440 + toMinutes(parseTime(entry.time)),
        time: entry.time,
        onOff: entry.onOff,
      });
    });
  });

  // 2) 릴레이별로 시각순 정렬 → 첫 ON 부터 on→off 페어링 (주 경계 순환)
  const miniCards = []; // { day, lights, timeSlots:[1] }
  Object.entries(eventsByRly).forEach(([rlyKey, events]) => {
    const lights = rlyKey
      .split(',')
      .map(n => parseInt(n.trim(), 10))
      .filter(n => !isNaN(n));

    const sorted = [...events].sort((a, b) => a.absMinute - b.absMinute);
    const firstOnIndex = sorted.findIndex(e => e.onOff === 'on');
    if (firstOnIndex === -1) return; // ON 이벤트가 없으면 스킵

    // 첫 ON 이 맨 앞에 오도록 회전 → 잘 구성된 스케줄은 on,off,on,off... 로 교대
    const rotated = [...sorted.slice(firstOnIndex), ...sorted.slice(0, firstOnIndex)];

    const pushSlot = (onEvent, offTime) => {
      const onDayKey = DAYS_ORDER[onEvent.dayIndex];
      miniCards.push({
        day: onDayKey,
        lights,
        timeSlots: [
          {
            id: `${onDayKey}-${rlyKey}-${onEvent.absMinute}`,
            lights,
            onTime: parseTime(onEvent.time),
            offTime,
          },
        ],
      });
    };

    let pendingOn = null;
    rotated.forEach(event => {
      if (event.onOff === 'on') {
        // off 없이 on 이 또 오면(겹침/비정상) 이전 on 은 00:00 으로 마감
        if (pendingOn) pushSlot(pendingOn, { hour: '00', minute: '00' });
        pendingOn = event;
      } else if (pendingOn) {
        pushSlot(pendingOn, parseTime(event.time));
        pendingOn = null;
      }
    });
    if (pendingOn) pushSlot(pendingOn, { hour: '00', minute: '00' });
  });

  // 3) 동일 (lights, timeSlots) 미니카드끼리 요일 묶음 (월~금 동일 스케줄 등)
  const groups = new Map(); // hash -> { days, lights, timeSlots }
  miniCards.forEach(mc => {
    const h = hashCardContent(mc.lights, mc.timeSlots);
    if (!groups.has(h)) groups.set(h, { days: [], lights: mc.lights, timeSlots: mc.timeSlots });
    groups.get(h).days.push(mc.day);
  });

  // 3) 최종 카드 배열 (요일은 월~일 순서)
  let idx = 0;
  return Array.from(groups.values()).map(group => {
    const sortedDays = [...group.days].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));
    const lightsKey = [...group.lights].sort((a, b) => a - b).join(',');
    idx += 1;
    return {
      id: `schedule-${sortedDays.join('_')}-${lightsKey}-${idx}`,
      days: sortedDays,
      timeSlots: group.timeSlots,
      isNew: false,
    };
  });
};

// 타임슬롯 깊은 복사 (id 새로 발급)
const cloneTimeSlots = (slots, prefix) =>
  slots.map((slot, i) => ({
    ...slot,
    id: `${prefix}-${i}-${Date.now()}`,
    lights: [...slot.lights],
    onTime: { ...slot.onTime },
    offTime: { ...slot.offTime },
    isNew: true,
  }));

const WeeklySchedule = ({ scheduleData, relayData, sensorStatusData, branchId, roomId, token }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // 편집 가능한 스케줄 (로컬 상태)
  const [editableSchedules, setEditableSchedules] = useState([]);
  // 카드별 접기 상태
  const [collapsedSchedules, setCollapsedSchedules] = useState({});
  // 시간 드롭다운 어떤 게 열려있는지 ('slotId-onHour' 같은 ID)
  const [openDropdown, setOpenDropdown] = useState(null);
  // 외부 클릭으로 드롭다운 닫기
  const wrapperRef = useRef(null);

  // 서버 데이터가 들어오면 로컬 상태 초기화
  useEffect(() => {
    setEditableSchedules(buildSchedulesFromServer(scheduleData));
  }, [scheduleData]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 사용 가능한 조명 채널 번호 (1부터)
  const availableLights = sensorStatusData?.channels
    ? Array.from({ length: Object.keys(sensorStatusData.channels).length }, (_, i) => i + 1)
    : [];

  // ============================================
  // 핸들러
  // ============================================
  const handleTimeChange = (scheduleId, slotId, timeType, field, value) => {
    setEditableSchedules(prev =>
      prev.map(schedule => {
        if (schedule.id !== scheduleId) return schedule;
        return {
          ...schedule,
          timeSlots: schedule.timeSlots.map(slot => {
            if (slot.id !== slotId) return slot;
            return { ...slot, [timeType]: { ...slot[timeType], [field]: value } };
          }),
        };
      }),
    );
    setOpenDropdown(null);
  };

  const handleLightToggle = (scheduleId, slotId, lightNum) => {
    setEditableSchedules(prev =>
      prev.map(schedule => {
        if (schedule.id !== scheduleId) return schedule;
        return {
          ...schedule,
          timeSlots: schedule.timeSlots.map(slot => {
            if (slot.id !== slotId) return slot;
            const lights = slot.lights.includes(lightNum)
              ? slot.lights.filter(l => l !== lightNum)
              : [...slot.lights, lightNum];
            return { ...slot, lights };
          }),
        };
      }),
    );
  };

  const handleAddTimeSlot = scheduleId => {
    setEditableSchedules(prev =>
      prev.map(schedule => {
        if (schedule.id !== scheduleId) return schedule;
        return {
          ...schedule,
          timeSlots: [
            ...schedule.timeSlots,
            {
              id: `slot-new-${Date.now()}`,
              lights: availableLights,
              onTime: { hour: '09', minute: '00' },
              offTime: { hour: '18', minute: '00' },
              isNew: true,
            },
          ],
        };
      }),
    );
  };

  const handleAddSchedule = () => {
    // 모든 카드가 점유한 요일들 (한 요일은 한 카드에만 속할 수 있음)
    const allTakenDays = editableSchedules.flatMap(s => s.days);
    const availableDays = DAYS_ORDER.filter(d => !allTakenDays.includes(d));

    if (availableDays.length === 0) {
      toast.error(t('모든 요일에 스케줄이 이미 존재합니다'));
      return;
    }

    const newSchedule = {
      id: `schedule-new-${Date.now()}`,
      days: [availableDays[0]],
      timeSlots: [
        {
          id: `slot-new-${Date.now()}`,
          lights: availableLights,
          onTime: { hour: '09', minute: '00' },
          offTime: { hour: '18', minute: '00' },
          isNew: true,
        },
      ],
      isNew: true,
    };
    setEditableSchedules(prev => [...prev, newSchedule]);
  };

  // 요일 토글 — 카드의 days 배열에 추가/제거 (다중 선택, 카드 간 중복 허용)
  // 같은 요일을 여러 카드가 가져도 OK (예: 월 09-12 카드 + 월 13-19 카드)
  // 모니터(전원) 중복은 저장 시점 검증에서 차단
  const handleDayToggle = (scheduleId, day) => {
    const target = editableSchedules.find(s => s.id === scheduleId);
    if (!target) return;
    const isAlreadyInThisCard = target.days.includes(day);

    // 마지막 한 요일은 해제 불가 (해제하려면 카드 자체를 삭제)
    if (isAlreadyInThisCard && target.days.length === 1) {
      toast.error(t('최소 한 개의 요일은 선택되어야 합니다'));
      return;
    }

    setEditableSchedules(prev =>
      prev.map(s => {
        if (s.id !== scheduleId) return s;
        const newDays = isAlreadyInThisCard
          ? s.days.filter(d => d !== day)
          : [...s.days, day].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));
        return { ...s, days: newDays };
      }),
    );
  };

  const handleToggleScheduleCollapse = scheduleId => {
    setCollapsedSchedules(prev => ({ ...prev, [scheduleId]: !prev[scheduleId] }));
  };

  const handleDeleteTimeSlot = (scheduleId, slotId) => {
    if (!window.confirm(t('해당 타임슬롯을 삭제하시겠습니까?'))) return;
    setEditableSchedules(prev =>
      prev.map(schedule => {
        if (schedule.id !== scheduleId) return schedule;
        return { ...schedule, timeSlots: schedule.timeSlots.filter(slot => slot.id !== slotId) };
      }),
    );
  };

  const handleDeleteSchedule = scheduleId => {
    if (!window.confirm(t('해당 요일의 모든 스케줄을 삭제하시겠습니까?'))) return;
    setEditableSchedules(prev => prev.filter(s => s.id !== scheduleId));
  };

  // 카드 복사 — 요일/시간/조명 설정 모두 그대로 복제 (사용자가 이후 수정)
  const handleCopySchedule = scheduleId => {
    const source = editableSchedules.find(s => s.id === scheduleId);
    if (!source) return;

    const newSchedule = {
      id: `schedule-new-${Date.now()}`,
      days: [...source.days],
      timeSlots: cloneTimeSlots(source.timeSlots, `slot-copy-${Date.now()}`),
      isNew: true,
    };
    setEditableSchedules(prev => [...prev, newSchedule]);
    toast.success(t('스케줄이 복사되었습니다'));
  };

  // ============================================
  // 저장 mutation
  // ============================================
  const saveScheduleMutation = useMutation({
    mutationFn: data => sendWithOfflineConfirm(force => saveIotSchedule(data, token, force), t(OFFLINE_SAVE_MESSAGE)),
    onSuccess: () => {
      toast.success(t('스케줄이 저장되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['iotSchedule', branchId, roomId] });
    },
    onError: error => {
      if (error?.silent) return;
      console.error('스케줄 저장 에러:', error);
      toast.error(errorMessages(error) || t('스케줄 저장에 실패했습니다'));
    },
  });

  const handleSaveAllSchedules = () => {
    // 1) 요일이 하나도 선택 안 된 카드 차단
    const noDayCard = editableSchedules.find(s => !s.days || s.days.length === 0);
    if (noDayCard) {
      toast.error(t('모든 카드에 최소 한 개의 요일을 선택해주세요'));
      return;
    }

    // 2) 타임슬롯이 없는 카드 차단
    const emptySchedule = editableSchedules.find(s => !s.timeSlots || s.timeSlots.length === 0);
    if (emptySchedule) {
      toast.error(t('모든 카드에 최소 하나의 타임슬롯을 추가해주세요'));
      return;
    }

    // 3) 조명이 없는 슬롯 차단
    for (const schedule of editableSchedules) {
      const noLightsSlot = schedule.timeSlots.find(slot => !slot.lights || slot.lights.length === 0);
      if (noLightsSlot) {
        const dayLabels = schedule.days.map(d => DAY_LABEL[d]).join(', ');
        toast.error(t(`${dayLabels}요일 스케줄에 조명을 선택해주세요`));
        return;
      }
    }

    // 4) 같은 요일·같은 전원에 대해 카드 간 시간대 겹침 차단
    //    OK: 전원1 09:00-12:00 + 전원1 13:00-18:00 (시간 안 겹침)
    //    OK: 시간대 끝과 시작이 같은 경우 (12:00 off + 12:00 on)
    //    NG: 전원1 09:00-12:00 + 전원1 11:00-18:00 (11~12 겹침)
    // 자정 넘김(익일 OFF) 슬롯은 [on, 1440) + 익일 [0, off) 두 구간으로 나눠 검사한다.
    const fmt = ({ hour, minute }) => `${hour}:${minute}`;

    // `${요일}|${전원}` -> [{ start, end, cardId, label }]
    const intervalsByDayLight = new Map();
    const addInterval = (dayKey, light, start, end, cardId, label) => {
      const key = `${dayKey}|${light}`;
      if (!intervalsByDayLight.has(key)) intervalsByDayLight.set(key, []);
      intervalsByDayLight.get(key).push({ start, end, cardId, label });
    };

    editableSchedules.forEach(card => {
      card.timeSlots.forEach(slot => {
        const on = toMinutes(slot.onTime);
        const off = toMinutes(slot.offTime);
        const label = `${fmt(slot.onTime)}~${fmt(slot.offTime)}`;
        const overnight = off <= on;
        card.days.forEach(dayKey => {
          slot.lights.forEach(light => {
            if (overnight) {
              addInterval(dayKey, light, on, 1440, card.id, label);
              if (off > 0) addInterval(nextDayKey(dayKey), light, 0, off, card.id, label);
            } else {
              addInterval(dayKey, light, on, off, card.id, label);
            }
          });
        });
      });
    });

    // 요일·전원별로 서로 다른 카드 구간끼리 겹침 검사 (같은 카드 내부는 사용자 의도로 skip)
    for (const [key, intervals] of intervalsByDayLight) {
      const [dayKey, light] = key.split('|');
      for (let i = 0; i < intervals.length; i++) {
        for (let j = i + 1; j < intervals.length; j++) {
          const a = intervals[i];
          const b = intervals[j];
          if (a.cardId === b.cardId) continue;
          // 겹침 조건: aStart < bEnd && bStart < aEnd (끝점만 일치하는 건 허용)
          if (a.start < b.end && b.start < a.end) {
            toast.error(t(`${DAY_LABEL[dayKey]}요일 전원 ${light}: ${a.label}와 ${b.label} 시간대가 겹칩니다`));
            return;
          }
        }
      }
    }

    // 카드별 → 서버 포맷(rlyTime)으로 변환
    // OFF 가 ON 보다 같거나 빠르면(자정 넘김) OFF 이벤트는 "다음 요일"에 넣는다.
    const rlyTime = {};
    const pushEvent = (dayKey, event) => {
      if (!rlyTime[dayKey]) rlyTime[dayKey] = [];
      rlyTime[dayKey].push(event);
    };
    editableSchedules.forEach(schedule => {
      schedule.timeSlots.forEach(slot => {
        const rlyString = [...slot.lights].sort((a, b) => a - b).join(',');
        const overnight = isOvernight(slot.onTime, slot.offTime);
        schedule.days.forEach(dayKey => {
          pushEvent(dayKey, { rly: rlyString, time: `${slot.onTime.hour}:${slot.onTime.minute}`, onOff: 'on' });
          const offDayKey = overnight ? nextDayKey(dayKey) : dayKey;
          pushEvent(offDayKey, { rly: rlyString, time: `${slot.offTime.hour}:${slot.offTime.minute}`, onOff: 'off' });
        });
      });
    });

    saveScheduleMutation.mutate({ branchId, roomId, rlyTime });
  };

  return (
    <Wrapper ref={wrapperRef}>
      <TopBar>
        <TopBarTitle>{t('요일별 예약 설정')}</TopBarTitle>
        {/* <StyledSwitch checked={editableSchedules.length > 0} disabled /> */}
      </TopBar>

      {editableSchedules.length > 0 && (
        <CardGrid>
          {editableSchedules.map(schedule => (
            <WeeklyScheduleCard
              key={schedule.id}
              schedule={schedule}
              relayData={relayData}
              availableLights={availableLights}
              isCollapsed={!!collapsedSchedules[schedule.id]}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onDayToggle={handleDayToggle}
              onTimeChange={handleTimeChange}
              onLightToggle={handleLightToggle}
              onAddTimeSlot={handleAddTimeSlot}
              onDeleteTimeSlot={handleDeleteTimeSlot}
              onDeleteSchedule={handleDeleteSchedule}
              onCopySchedule={handleCopySchedule}
              onToggleCollapse={handleToggleScheduleCollapse}
            />
          ))}
        </CardGrid>
      )}

      {editableSchedules.length === 0 && <EmptyMessage>{t('설정된 스케줄이 없습니다')}</EmptyMessage>}

      <Footer>
        <AddScheduleButton type="button" onClick={handleAddSchedule}>
          <AddCircleOutlineOutlinedIcon style={{ fontSize: 24, color: '#A6A6A6' }} />
          {t('추가하기')}
        </AddScheduleButton>
        <SaveButton type="button" disabled={saveScheduleMutation.isPending} onClick={handleSaveAllSchedules}>
          {saveScheduleMutation.isPending ? t('저장 중...') : t('전체 저장')}
        </SaveButton>
      </Footer>
    </Wrapper>
  );
};

export default WeeklySchedule;
